import { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { rootApi } from 'root-api'

const NOTETAKER_SHARES_V2 = '/api/notetaker/v2/shares'

export const MAX_FETCH_SHARES_BATCH_SIZE = 50

const unwrapShareResource = (response) => response?.resource ?? response

const shareTag = (eventUuid) => ({ type: 'sharesV2', id: eventUuid })

const BATCH_FETCH_SHARES_BY_EVENT_UUID_V2 = 'batchFetchSharesByEventUuidV2'

const chunkArrayBy = (arr, chunkSize) => {
  const result = []
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize))
  }
  return result
}

const patchCachedBatchShares = ({ dispatch, getState }, eventUuid, updateCachedShares) => {
  const cachedBatchArgs = rootApi.util.selectCachedArgsForQuery(getState(), BATCH_FETCH_SHARES_BY_EVENT_UUID_V2) || []

  cachedBatchArgs.forEach((cachedArgs) => {
    if (!cachedArgs?.includes?.(eventUuid)) return

    dispatch(rootApi.util.updateQueryData(
      BATCH_FETCH_SHARES_BY_EVENT_UUID_V2,
      cachedArgs,
      updateCachedShares
    ))
  })
}

const upsertShareInBatch = (draft, share) => {
  if (!Array.isArray(draft) || !share?.eventUuid) return

  const shareIndex = draft.findIndex((cachedShare) => cachedShare.eventUuid === share.eventUuid)

  if (shareIndex === -1) {
    draft.push(share)
    return
  }

  draft[shareIndex] = share
}

const removeShareFromBatch = (draft, eventUuid) => {
  if (!Array.isArray(draft)) return

  const shareIndex = draft.findIndex((cachedShare) => cachedShare.eventUuid === eventUuid)

  if (shareIndex !== -1) {
    draft.splice(shareIndex, 1)
  }
}

// sharesV2Batch is purposely not invalidate when share is created or deleted
// we will handle the cache update in the onQueryStarted
const sharesV2Api = rootApi
  .enhanceEndpoints({ addTagTypes: ['sharesV2', 'sharesV2Batch'] })
  .injectEndpoints({
    endpoints: (build) => ({
      postSharesV2: build.mutation({
        query: ({ event_uuid: eventUuid, access_level: accessLevel }) => ({
          url: NOTETAKER_SHARES_V2,
          method: 'POST',
          data: { event_uuid: eventUuid, access_level: accessLevel },
        }),
        transformResponse: unwrapShareResource,
        invalidatesTags: (_result, error, { event_uuid: eventUuid }) => (
          error ? [] : [shareTag(eventUuid)]
        ),
        onQueryStarted: async ({ event_uuid: eventUuid }, api) => {
          try {
            const { data: share } = await api.queryFulfilled
            patchCachedBatchShares(api, eventUuid, (draft) => upsertShareInBatch(draft, share))
          } catch {
            // The mutation error path is handled by the caller.
          }
        },
      }),
      fetchSharesByEventUuidV2: build.query({
        query: (eventUuid) => ({
          url: `${NOTETAKER_SHARES_V2}?event_uuid=${eventUuid}`,
          validateStatus: (response) => (['200', '404'].includes(`${response}`)),
        }),
        transformResponse: unwrapShareResource,
        providesTags: (_result, _error, eventUuid) => [shareTag(eventUuid)],
        extraOptions: {
          handleErrors: false,
        },
      }),
      batchFetchSharesByEventUuidV2: build.query({
        query: (eventUuids) => ({
          url: `${NOTETAKER_SHARES_V2}/batch_lookup`,
          method: 'POST',
          data: { event_uuids: eventUuids },
          validateStatus: (response) => (['200', '403'].includes(`${response}`)),
        }),
        providesTags: ['sharesV2Batch'],
        extraOptions: {
          handleErrors: false,
        },
      }),
      destroySharesV2: build.mutation({
        query: ({ event_uuid: eventUuid }) => ({
          url: `${NOTETAKER_SHARES_V2}?event_uuid=${eventUuid}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, error, { event_uuid: eventUuid }) => (
          error ? [] : [shareTag(eventUuid)]
        ),
        onQueryStarted: async ({ event_uuid: eventUuid }, api) => {
          try {
            await api.queryFulfilled
            patchCachedBatchShares(api, eventUuid, (draft) => removeShareFromBatch(draft, eventUuid))
          } catch {
            // The mutation error path is handled by the caller.
          }
        },
      }),
    }),
    overrideExisting: true,
  })

export const useBatchedRecapShares = (eventUuids) => {
  const dispatch = useDispatch()
  const chunks = useMemo(() => {
    if (!eventUuids || eventUuids.length === 0) {
      return []
    }

    return chunkArrayBy(eventUuids, MAX_FETCH_SHARES_BATCH_SIZE)
  }, [eventUuids])

  const queryStates = useSelector((state) => chunks.map((chunk) => (
    rootApi.endpoints.batchFetchSharesByEventUuidV2.select(chunk)(state)
  )), shallowEqual)

  useEffect(() => {
    if (chunks.length === 0) {
      return undefined
    }

    const queryPromises = chunks.map((chunk) => dispatch(
      rootApi.endpoints.batchFetchSharesByEventUuidV2.initiate(chunk)
    ))

    return () => {
      queryPromises.forEach((queryPromise) => queryPromise?.unsubscribe?.())
    }
  }, [chunks, dispatch])

  const shares = useMemo(() => (
    queryStates.flatMap((queryState) => queryState?.data || [])
  ), [queryStates])

  const isLoading = chunks.length > 0 && queryStates.some((queryState) => (
    queryState?.isLoading || queryState?.isFetching || queryState?.isUninitialized
  ))

  return { shares, isLoading }
}

export const {
  usePostSharesV2Mutation,
  useFetchSharesByEventUuidV2Query,
  useLazyFetchSharesByEventUuidV2Query,
  useDestroySharesV2Mutation,
} = sharesV2Api
